class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  cartUpdateUnsubscriber = undefined;
  _headerTotalUpdateInFlight = null;
  _headerTotalLastUpdateAt = 0;

  connectedCallback() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-drawer' || event.source === 'cart-drawer-items') {
          return;
        }
        // Snapshot before anything below repaints the cart icon bubble
        const wasEmpty = this.wasCartEmpty();
        if (event.cartData) {
          if (event.cartData.sections) {
            if (event.cartData.sections['cart-icon-bubble']) {
              this.updateCartIconBubble(event.cartData.sections);
            }
            if (event.cartData.sections['cart-drawer']) {
              this.updateCartDrawerContent(event.cartData.sections);
            }
            // Header total is in section id "header" (from header-group.json) → update it explicitly
            this.updateHeaderCartTotal();
          } else if (event.cartData.item_count !== undefined) {
            this.updateCartCountDirectly(event.cartData.item_count);
            const cartDrawerItems = this.querySelector('cart-drawer-items');
            if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
              cartDrawerItems.onCartUpdate();
            }
            this.updateHeaderCartTotal();
          } else {
            // If no item_count, try to fetch cart to get updated count
            this.fetchCartAndUpdate();
            this.updateHeaderCartTotal();
          }
        } else {
          // If no cartData at all, fetch cart to get updated count
          this.fetchCartAndUpdate();
          this.updateHeaderCartTotal();
        }

        if (wasEmpty) this.openAfterFirstAdd(event.cartData);
      });
    }
  }

  wasCartEmpty() {
    const iconBubble = document.querySelector('#cart-icon-bubble');
    if (iconBubble) return !iconBubble.querySelector('.cart-count-bubble');
    return this.classList.contains('is-empty');
  }

  // Confirms the add by sliding out, but only when the basket went from empty to filled
  openAfterFirstAdd(cartData) {
    if (!cartData || this.classList.contains('active')) return;
    if (cartData.item_count === 0) return;

    setTimeout(() => this.open());
  }

  updateCartCountDirectly(itemCount) {
    // Update all cart icon bubbles (there can be multiple - one in main header, one in sticky header)
    const cartIconBubbles = document.querySelectorAll('#cart-icon-bubble');
    
    cartIconBubbles.forEach((cartIconBubble) => {
      const cartCountBubble = cartIconBubble.querySelector('.cart-count-bubble');
      
      if (itemCount > 0) {
        if (!cartCountBubble) {
          const bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          const countSpan = document.createElement('span');
          countSpan.setAttribute('aria-hidden', 'true');
          countSpan.textContent = itemCount < 100 ? itemCount : '';
          bubble.appendChild(countSpan);
          const hiddenSpan = document.createElement('span');
          hiddenSpan.className = 'visually-hidden';
          hiddenSpan.textContent = `${itemCount} items in cart`;
          bubble.appendChild(hiddenSpan);
          cartIconBubble.appendChild(bubble);
        } else {
          const countSpan = cartCountBubble.querySelector('span[aria-hidden="true"]');
          if (countSpan) {
            countSpan.textContent = itemCount < 100 ? itemCount : '';
          }
          const hiddenSpan = cartCountBubble.querySelector('.visually-hidden');
          if (hiddenSpan) {
            hiddenSpan.textContent = `${itemCount} items in cart`;
          }
        }
      } else {
        if (cartCountBubble) {
          cartCountBubble.remove();
        }
      }
    });

    // Also update cart drawer basket count
    const basketCountElements = this.querySelectorAll('.cart-drawer__basket-count');
    basketCountElements.forEach((element) => {
      element.textContent = itemCount;
      if (itemCount === 0) {
        element.style.display = 'none';
      } else {
        element.style.display = 'flex';
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async fetchCartAndUpdate() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cart = await response.json();
      if (cart && cart.item_count !== undefined) {
        this.updateCartCountDirectly(cart.item_count);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  async updateHeaderCartTotal() {
    // Throttle (avoid spamming requests on rapid adds)
    const now = Date.now();
    if (this._headerTotalUpdateInFlight) return this._headerTotalUpdateInFlight;
    if (now - this._headerTotalLastUpdateAt < 150) return;

    this._headerTotalLastUpdateAt = now;

    this._headerTotalUpdateInFlight = (async () => {
      try {
        const url = new URL(window.location.href);
        // NOTE: sections rendering API expects a section id, not section group id.
        // In this theme header lives under id "header" in sections/header-group.json
        url.searchParams.set('sections', 'header');
        url.searchParams.set('sections_url', window.location.pathname);

        const res = await fetch(url.toString(), {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });
        const sections = await res.json();
        const headerHTML = sections?.header;
        if (!headerHTML) return;

        const doc = new DOMParser().parseFromString(headerHTML, 'text/html');
        const newTotalEl = doc.querySelector('.header__cart-total');
        if (!newTotalEl) return;

        const newValue = newTotalEl.innerHTML;
        document.querySelectorAll('.header__cart-total').forEach((el) => {
          el.innerHTML = newValue;
        });
      } catch (e) {
        // Don't break cart interactions if header refresh fails
        console.warn('Failed to update header cart total', e);
      } finally {
        this._headerTotalUpdateInFlight = null;
      }
    })();

    return this._headerTotalUpdateInFlight;
  }

  updateCartIconBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) {
      // If sections not available, try to update from cartData
      return;
    }
    
    // Update all cart icon bubbles (there can be multiple - one in main header, one in sticky header)
    const cartIconBubbleElements = document.querySelectorAll('#cart-icon-bubble');
    if (cartIconBubbleElements.length === 0) return;

    const sectionHTML = sections['cart-icon-bubble'];
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHTML, 'text/html');
    const newContent = doc.querySelector('#cart-icon-bubble') || doc.querySelector('.shopify-section');
    
    if (newContent) {
      // Update all cart icon bubbles with the same content
      cartIconBubbleElements.forEach((cartIconBubbleElement) => {
        // Remove data attribute to allow re-initialization
        delete cartIconBubbleElement.dataset.cartDrawerInitialized;
        cartIconBubbleElement.innerHTML = newContent.innerHTML;
      });
      // Re-initialize cart icon accessibility after update
      this.setHeaderCartIconAccessibility();
      // Update cart drawer basket count
      this.updateCartDrawerBasketCount(sections);
    }
  }

  updateCartDrawerContent(sections) {
    const cartDrawerSection = sections['cart-drawer'];
    if (!cartDrawerSection) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(cartDrawerSection, 'text/html');
    const newContent = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');

    if (newContent) {
      const currentDrawerInner = this.querySelector('.drawer__inner');
      if (currentDrawerInner) {
        currentDrawerInner.innerHTML = newContent.innerHTML;

        if (currentDrawerInner.classList.contains('is-empty')) {
          currentDrawerInner.classList.remove('is-empty');
        }
        if (this.classList.contains('is-empty')) {
          this.classList.remove('is-empty');
        }
      }
    }

    this.updateCartDrawerBasketCount(sections);
  }

  updateCartDrawerBasketCount(sections) {
    // Extract item_count from sections if available
    const cartDrawerSection = sections['cart-drawer'];
    if (!cartDrawerSection) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(cartDrawerSection, 'text/html');
    const itemCountMatch = doc.body.textContent.match(/item_count["\s:]*(\d+)/);
    if (!itemCountMatch) return;

    const itemCount = parseInt(itemCountMatch[1], 10);
    if (isNaN(itemCount)) return;

    // Update basket count in cart drawer
    const basketCountElements = this.querySelectorAll('.cart-drawer__basket-count');
    basketCountElements.forEach((element) => {
      element.textContent = itemCount;
      if (itemCount === 0) {
        element.style.display = 'none';
      } else {
        element.style.display = 'flex';
      }
    });
  }

  setHeaderCartIconAccessibility() {
    // Update all cart icon bubbles (there can be multiple - one in main header, one in sticky header)
    const cartLinks = document.querySelectorAll('#cart-icon-bubble');
    if (cartLinks.length === 0) return;

    cartLinks.forEach((cartLink) => {
      // Check if already has event listeners (has data attribute)
      if (cartLink.dataset.cartDrawerInitialized === 'true') {
        return;
      }
      
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.dataset.cartDrawerInitialized = 'true';
      
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });

    this.setHeaderCartTotalAccessibility(cartLinks);
  }

  setHeaderCartTotalAccessibility(cartLinks) {
    const headerTotals = document.querySelectorAll('.header__cart-total');
    if (headerTotals.length === 0) return;

    headerTotals.forEach((totalElement) => {
      if (totalElement.dataset.cartDrawerInitialized === 'true') {
        return;
      }

      const relatedCartLink =
        totalElement.closest('.header__cart-wrapper')?.querySelector('#cart-icon-bubble') || cartLinks[0];
      if (!relatedCartLink) return;

      totalElement.setAttribute('role', 'button');
      totalElement.setAttribute('tabindex', '0');
      totalElement.setAttribute('aria-haspopup', 'dialog');
      totalElement.dataset.cartDrawerInitialized = 'true';

      totalElement.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(relatedCartLink);
      });

      totalElement.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(relatedCartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
